<script setup lang="ts">
/**
 * StatsSection —— `.scu-widget` 壳 + 辅助数据展示（2026-06-25 R1/R6）。
 *
 * 模板无一一对应区，复用 `.scu-widget` + `.scu-grid-item` 视觉语言：
 * SectionCard 提供 widget 壳，图片网格走 scu-grid-item 风格卡片，
 * 后接 `<RichText>` 正文。
 */
import { computed } from 'vue';

import SectionCard from './SectionCard.vue';
import FallbackImage from '@/components/FallbackImage.vue';
import RichTextViewer from '@/components/RichTextViewer.vue';
import type { SectionComponentProps } from './section-props';
import { hasText, sectionImages } from './section-presenters';

const props = defineProps<SectionComponentProps>();
const images = computed(() => sectionImages(props.section));
</script>

<template>
  <SectionCard
    :id="props.id"
    type="stats"
    :title="props.section.title"
    :summary="props.section.summary"
    :show-draft-badge="props.showDraftBadge"
  >
    <div v-if="images.length > 0" :class="$style.grid">
      <figure
        v-for="(image, index) in images"
        :key="`${image.url}-${index}`"
        :class="$style.item"
      >
        <FallbackImage
          :src="image.url"
          :alt="hasText(image.caption) ? image.caption : props.section.title"
          :context="`stats section='${props.section.id}' image#${index}`"
        />
      </figure>
    </div>
    <RichTextViewer
      v-if="hasText(props.section.bodyHtml)"
      :html="props.section.bodyHtml"
      as="article"
    />
  </SectionCard>
</template>

<style module>
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(var(--section-image-card-min), 1fr));
  gap: var(--space-2);
  margin-bottom: var(--space-2);
}

.item {
  margin: 0;
  overflow: hidden;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition:
    box-shadow var(--template-hover-duration) ease,
    transform var(--template-hover-duration) ease,
    border-color var(--template-hover-duration) ease;
}

.item:hover {
  border-color: var(--color-brand-primary);
  box-shadow: var(--shadow-card);
  transform: translateY(var(--template-hover-lift));
}

.item img {
  width: 100%;
  display: block;
}
</style>
